/**
 * Cabeçalho de coluna que ordena a tabela ao ser clicado.
 *
 * Papel: um `<th>` com um botão que ocupa a célula inteira, a seta "▲"/"▼" da
 * coluna ativa e o `aria-sort` que diz a leitores de tela qual é a ordem.
 *
 * Como usar: a aparência da célula — fonte, fundo, borda, hover — é do kit de
 * cada módulo e entra por `className`; a seta segue `.sort-arrow` da origem
 * (`margin-left:4px; opacity:.8; font-size:10px`, l. 409) e pode ser ajustada
 * por `classeDaSeta`. O clique fica num `<button>`, e não no `<th>`, porque só
 * assim a coluna é alcançável pelo teclado; o botão herda fonte, cor e
 * alinhamento da célula e não desenha nada próprio.
 *
 * A tabela montada com o `Table` de `@/shared/ui/table` passa `como={TableHead}`
 * para a célula sair com o visual do cabeçalho de lá, em vez de repetir as
 * classes dele em `className`. O `title` ("Ordenar por …") entra como qualquer
 * outro atributo do `<th>`.
 */
import { type ComponentProps, type ComponentType } from 'react';

import { cn } from '@/shared/lib/utils';
import { type SetaDeOrdenacao } from './use-ordenacao';

/**
 * A seta como as tabelas de Documentos, Saúde e RH da origem a escrevem: um
 * espaço e o "▲"/"▼" colados ao rótulo, na fonte, no tamanho e na cor da
 * própria célula, sem a margem, a letra menor e a opacidade de `.sort-arrow`.
 *
 * Quem desenha uma dessas tabelas passa esta constante em `classeDaSeta`; sem
 * ela, a seta sai com o desenho da origem de Manutenções, menor e mais clara,
 * e 3 px mais perto do rótulo.
 */
export const SETA_EM_TEXTO = "ml-0 text-[length:inherit] opacity-100 before:content-['_']";

interface CabecalhoOrdenavelProps extends Omit<ComponentProps<'th'>, 'onClick'> {
  /** A seta que `useOrdenacao().seta(coluna)` devolveu. */
  seta: SetaDeOrdenacao;
  /** Chamado no clique; quem monta chama `useOrdenacao().alternar(coluna)`. */
  onAlternar: () => void;
  /** Classes da seta, aplicadas depois das internas. */
  classeDaSeta?: string;
  /** A célula a desenhar; sem ela, um `<th>` sem estilo próprio. */
  como?: 'th' | ComponentType<ComponentProps<'th'>>;
}

/**
 * Um cabeçalho ordenável.
 *
 * @param seta - A seta da coluna, ou `null` quando ela não é a ativa.
 * @param onAlternar - Chamado a cada clique.
 * @param classeDaSeta - Classes da seta de quem chama.
 * @param como - O componente da célula, que recebe `className`, `scope`,
 *   `aria-sort` e o botão; o padrão é o `<th>` puro.
 * @param className - Classes do `<th>`, vindas do kit do módulo.
 * @param children - O rótulo da coluna, como a origem o escreve.
 * @returns O `<th>` com o botão.
 */
export function CabecalhoOrdenavel({
  seta,
  onAlternar,
  classeDaSeta,
  como: Celula = 'th',
  className,
  children,
  ...props
}: CabecalhoOrdenavelProps) {
  const ordem = seta === null ? 'none' : seta === '▲' ? 'ascending' : 'descending';

  return (
    <Celula
      {...props}
      scope="col"
      aria-sort={ordem}
      className={cn('relative cursor-pointer select-none', className)}
    >
      <button
        type="button"
        onClick={onAlternar}
        className="w-full cursor-pointer border-0 bg-transparent p-0 [text-align:inherit] [letter-spacing:inherit] text-inherit [text-transform:inherit] [font:inherit] after:absolute after:inset-0"
      >
        {children}
        {seta !== null && (
          <span aria-hidden="true" className={cn('ml-1 text-[10px] opacity-80', classeDaSeta)}>
            {seta}
          </span>
        )}
      </button>
    </Celula>
  );
}
